// Styles
// vuetify/styles is imported from src/assets/app.css, inside the `vuetify`
// cascade layer, so Tailwind utilities and our own component styles can win
// on a collision. Do not import it here too — see app.css for why.
//
// Icons stay on @mdi/font (class-based, e.g. icon="mdi-refresh") rather than
// mdi-svg + @mdi/js. Switching requires replacing every `mdi-*` string with
// an imported path constant — Vuetify's mdi-svg iconset only ships its own
// internal aliases (collapse/close/delete/...), not a name->path lookup for
// the full icon set — so it is a real ~90-usage mechanical migration across
// 27 files, not a drop-in config change. Deferred to its own pass with
// visual verification; see the project plan.
import '@mdi/font/css/materialdesignicons.css'

// Vuetify
import { createVuetify } from 'vuetify'
import { md3 } from 'vuetify/blueprints'
import { tokens } from '@/design/tokens'

export default createVuetify({
  blueprint: md3,
  theme: {
    // Only `light` is defined: nothing in the app ever calls useTheme() to
    // switch themes, so a `dark` theme block was previously dead weight —
    // shipped in the bundle but structurally unreachable.
    defaultTheme: 'light',
    themes: {
      light: {
        colors: {
          // `accent` (deep cyan, tokens.ts) drives every interactive
          // element — buttons, links, focus rings — and passes WCAG AA on
          // white. `primary` is aliased to it so existing `color="primary"`
          // usages inherit the fix without a mass find-and-replace.
          primary: tokens.accent,
          accent: tokens.accent,
          // Electric brand cyan — restricted to dark hero/CTA bands, see
          // tokens.ts. Used to make the primary CTA on the dark hero pop
          // more than `primary`'s AA-on-white teal, which reads muted there.
          brand: tokens.brandCyan,
          // `info` previously equalled `primary`/`accent`, so the three
          // semantic slots carried no distinct meaning. Given its own value.
          info: '#2563EB',
          secondary: '#0A0A0A', // Charcoal/near-black — dark hero/CTA bands
          error: tokens.negative,
          success: tokens.positive,
          warning: tokens.caution,
          background: tokens.surfacePage,
          surface: tokens.surfaceCard,
        },
      },
    },
  },
  defaults: {
    VCard: { rounded: 'lg', elevation: 0, border: true },
    VSheet: { rounded: 'lg', elevation: 0, border: true },
    VBtn: {
      rounded: 'lg',
      flat: true,
      class: 'text-none font-weight-medium',
      height: 40,
    },
    VDataTable: { density: 'comfortable', hover: true, itemsPerPage: 25 },
    VTable: { density: 'comfortable' },
    // 'border' is a design token, not a Vuetify theme colour slot — pass the
    // hex value directly rather than a name Vuetify can't resolve.
    VDivider: { color: tokens.border, opacity: 1 },
    VChip: { rounded: 'sm', size: 'small', variant: 'tonal' },
    VTextField: { variant: 'outlined', density: 'comfortable', hideDetails: 'auto' },
    VSelect: { variant: 'outlined', density: 'comfortable', hideDetails: 'auto' },
    VProgressCircular: { color: 'primary', size: 28, width: 3, indeterminate: true },
  },
})
